import React, { useCallback, useDeferredValue, useEffect, useMemo, useState } from 'react';
import {
  Campaign,
  CampaignAttendanceMode,
  CampaignItem,
  CreateCampaignRequest,
  Disparo,
  Usuario,
  WhatsAppAccount,
} from '../../types';
import { apiService } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { buildFullCampaignDispatchPreview } from './campaignPreview';
import { formatWhatsappSelectLabel } from '../../utils/whatsappAccountLabels';
import { CampaignWizard } from '../CampaignWizard/CampaignWizard';
import { CampaignItemsListPanel } from '../../components/CampaignItemsListPanel/CampaignItemsListPanel';
import {
  campaignDispatchProgressPercent,
  campaignOriginLabels,
  campaignTotalLeadCount,
} from '../../utils/campaignUi';
import {
  buildPrimaryDisparoByCampaignMap,
  campaignListDisplayBadge,
  campaignListDisplayKind,
  pickPrimaryDisparoForCampaign,
  type CampaignListDisplayKind,
} from '../../utils/campaignListDisplayStatus';
import { aggregateCampaignItemSourceTypes } from '../../utils/campaignSourcesAggregate';
import { Ban, Copy, Filter, Pause, Play } from 'lucide-react';
import './CampaignManager.css';

type ListPeriod = 'today' | 'week' | 'month' | 'year' | 'all';

const LIST_PERIOD_LABELS: Record<ListPeriod, string> = {
  today: 'Hoje',
  week: 'Semana',
  month: 'Mês',
  year: 'Ano',
  all: 'Todos',
};

const USER_ROLE_LABELS: Record<Usuario['role'], string> = {
  owner: 'Owner',
  admin: 'Admin',
  gerente: 'Gerente',
  atendente: 'Atendente',
  super_admin: 'Super Admin',
};

const USER_ROLE_PRIORITY: Record<Usuario['role'], number> = {
  owner: 0,
  admin: 1,
  gerente: 2,
  atendente: 3,
  super_admin: 4,
};

const ROLES_MAY_DELEGATE_CAMPAIGN_VISIBILITY: Usuario['role'][] = [
  'owner',
  'admin',
  'gerente',
  'super_admin',
];

const UNSUBSCRIBE_GLOBAL_HELP =
  'O texto do rodapé e a URL base do link são definidos nas Configurações da organização (única para todos os envios). ' +
  'Inclui um link para a pessoa parar de receber mensagens promocionais por este número. ' +
  'No WhatsApp a URL precisa aparecer no texto para ficar clicável.';

const DEFAULT_PRINCIPAL_CALL_INTRO = 'Clique no link para falar com nosso consultor:';

const PRINCIPAL_WHATSAPP_HELP =
  'Você pode usar {usePrincipalWhatsapp} no texto da mensagem para inserir o link https://wa.me/… da conta WhatsApp principal do membro. ' +
  'Com "Adicionar Ligue para" ativo, o mesmo placeholder é adicionado após o texto introdutório, entre a mensagem e o rodapé de descadastro. ' +
  'Se o link não puder ser resolvido, esse trecho é removido do envio.';

const DISPATCH_WHATSAPP_HELP =
  'Linha padrão para novos disparos desta campanha (pré-selecionada ao criar o disparo). Administradores e gerentes podem escolher qualquer conta da organização; atendentes só veem as linhas do próprio perfil. No disparo ainda é possível escolher outra conta permitida, se não houver outro disparo ativo nesse número. Entre parênteses: Principal e/ou Campanha conforme o vínculo em Membros.';

const POS_VENDAS_DISPATCH_HELP =
  'Por omissão, o disparador não envia mensagem quando o número corresponde a um lead em pós-vendas ativo na organização (já cliente). Marque para permitir enviar mesmos assim.';

function startOfDayIsoDate(isoDate: string): number {
  return new Date(`${isoDate}T00:00:00`).getTime();
}

function endOfDayIsoDate(isoDate: string): number {
  return new Date(`${isoDate}T23:59:59.999`).getTime();
}

function campaignCreatedInOptionalRange(c: Campaign, from: string, to: string): boolean {
  const t = new Date(c.createdAt).getTime();
  if (Number.isNaN(t)) return false;
  if (from.trim() && t < startOfDayIsoDate(from.trim())) return false;
  if (to.trim() && t > endOfDayIsoDate(to.trim())) return false;
  return true;
}

function messageSnippet(msg: string, maxLen = 72): string {
  const t = (msg || '').replace(/\s+/g, ' ').trim();
  if (!t) return '—';
  return t.length <= maxLen ? t : `${t.slice(0, maxLen)}…`;
}

function initialsFromNome(nome: string): string {
  const parts = nome.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function formatDisparoIntervaloLabel(minutos: number): string {
  if (!Number.isFinite(minutos) || minutos < 1) return '—';
  return minutos === 1 ? '1 min (~20–60 s entre envios)' : `${Math.round(minutos)} min`;
}

/** Rascunho novo com os mesmos campos gerais do assistente (passo 1). */
function buildCreateCampaignDuplicateBody(source: Campaign): CreateCampaignRequest {
  const baseNome = (source.nome || 'Campanha').trim() || 'Campanha';
  const nome = `${baseNome} (cópia)`.slice(0, 180);
  const wa = (source.whatsappAccountId || '').trim();
  const uid = (source.principalWhatsappUsuarioId || '').trim();
  const explicitFlag =
    source.principalCallBlockEnabled === true || source.principalCallBlockEnabled === false;
  const blockOn =
    source.principalCallBlockEnabled === true || (uid.length > 0 && !explicitFlag);
  const body: CreateCampaignRequest = {
    status: 'rascunho',
    nome,
    mensagem: (source.mensagem || '').trim(),
  };
  if (wa) body.whatsappAccountId = wa;
  if (blockOn) {
    body.principalCallBlockEnabled = true;
    if (uid) body.principalWhatsappUsuarioId = uid;
    const intro = (source.principalCallIntroText || '').trim();
    if (intro) body.principalCallIntroText = intro;
    body.delegateManagementToPrincipalMember = !!source.delegateManagementToPrincipalMember;
  } else {
    body.principalCallBlockEnabled = false;
    body.delegateManagementToPrincipalMember = false;
  }
  if (source.unsubscribeUseGlobal !== false) {
    body.unsubscribeUseGlobal = true;
  } else {
    body.unsubscribeUseGlobal = false;
    body.unsubscribeOverrideEnabled = true;
    body.unsubscribeBaseUrl = '';
    body.unsubscribeFooterTemplate = source.unsubscribeFooterTemplate || '';
  }
  if (source.allowActivePosVendasDispatch) {
    body.allowActivePosVendasDispatch = true;
  }
  if (source.defaultDisparoIntervaloMinutos != null && Number.isFinite(source.defaultDisparoIntervaloMinutos)) {
    body.defaultDisparoIntervaloMinutos = source.defaultDisparoIntervaloMinutos;
  }
  if (
    source.defaultDisparoMaxDisparosPorNumero != null &&
    Number.isFinite(source.defaultDisparoMaxDisparosPorNumero)
  ) {
    body.defaultDisparoMaxDisparosPorNumero = source.defaultDisparoMaxDisparosPorNumero;
  }
  if (source.defaultDisparoHoraInicio) {
    body.defaultDisparoHoraInicio = source.defaultDisparoHoraInicio;
  }
  if (source.defaultDisparoHoraFim) {
    body.defaultDisparoHoraFim = source.defaultDisparoHoraFim;
  }
  if (source.defaultDisparoAutoResumeOnMax === true) {
    body.defaultDisparoAutoResumeOnMax = true;
  }
  return body;
}

type ManagerView = 'list' | 'detail';
type DetailTab = 'info' | 'leads';
type StatusFilter =
  | 'all'
  | 'rascunho'
  | 'encerrada'
  | 'list_pendente'
  | 'list_em_execucao'
  | 'list_finalizada';

interface CampaignManagerProps {
  /** Após enviar campanha para pendentes, navega para o kanban de execução */
  onOpenExecution?: () => void;
}

export const CampaignManager: React.FC<CampaignManagerProps> = ({ onOpenExecution }) => {
  const { usuario, organization } = useAuth();
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [feedback, setFeedback] = useState('');

  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardEditId, setWizardEditId] = useState<string | null>(null);

  const [detailDelegateManagementToPrincipalMember, setDetailDelegateManagementToPrincipalMember] =
    useState(false);
  const [waMembers, setWaMembers] = useState<Usuario[]>([]);
  const [waMembersLoadFailed, setWaMembersLoadFailed] = useState(false);
  const [waAccounts, setWaAccounts] = useState<WhatsAppAccount[]>([]);
  const [detailDispatchWhatsappAccountId, setDetailDispatchWhatsappAccountId] = useState('');
  const [detailAllowActivePosVendasDispatch, setDetailAllowActivePosVendasDispatch] = useState(false);
  const [detailAttendanceMode, setDetailAttendanceMode] = useState<CampaignAttendanceMode>('human_kanban');

  const [selectedCampaignId, setSelectedCampaignId] = useState<string | null>(null);
  const [detailNome, setDetailNome] = useState('');
  const [detailMensagem, setDetailMensagem] = useState('');
  const [detailUseGlobalUnsubscribeConfig, setDetailUseGlobalUnsubscribeConfig] = useState(true);
  const [detailUnsubscribeFooterTemplate, setDetailUnsubscribeFooterTemplate] = useState('');
  const [detailPrincipalBlock, setDetailPrincipalBlock] = useState(false);
  const [detailPrincipalIntro, setDetailPrincipalIntro] = useState('');
  const [detailPrincipalUsuarioId, setDetailPrincipalUsuarioId] = useState('');
  const [listPeriod, setListPeriod] = useState<ListPeriod>('today');
  const [managerView, setManagerView] = useState<ManagerView>('list');
  const [detailTab, setDetailTab] = useState<DetailTab>('info');
  const [detailCampaignItems, setDetailCampaignItems] = useState<CampaignItem[]>([]);
  const [detailItemsLoading, setDetailItemsLoading] = useState(false);
  const [detailInfoEditing, setDetailInfoEditing] = useState(false);
  const [detailDisparos, setDetailDisparos] = useState<Disparo[]>([]);
  const [detailDisparosLoading, setDetailDisparosLoading] = useState(false);
  const [listSearch, setListSearch] = useState('');
  const deferredListSearch = useDeferredValue(listSearch.trim().toLowerCase());
  const [advancedFiltersOpen, setAdvancedFiltersOpen] = useState(false);
  const [filterStatus, setFilterStatus] = useState<StatusFilter>('all');
  const [filterCreatedFrom, setFilterCreatedFrom] = useState('');
  const [filterCreatedTo, setFilterCreatedTo] = useState('');
  const [filterUserId, setFilterUserId] = useState('');
  const [filterWhatsappAccountId, setFilterWhatsappAccountId] = useState('');
  const [listAggregateItems, setListAggregateItems] = useState<CampaignItem[]>([]);
  const [listAggregateDisparos, setListAggregateDisparos] = useState<Disparo[]>([]);

  const selectedCampaign = useMemo(
    () => campaigns.find((c) => c.campaignId === selectedCampaignId) ?? null,
    [campaigns, selectedCampaignId]
  );

  const listItemsByCampaignId = useMemo(() => {
    const m = new Map<string, CampaignItem[]>();
    for (const it of listAggregateItems) {
      if (!m.has(it.campaignId)) m.set(it.campaignId, []);
      m.get(it.campaignId)!.push(it);
    }
    return m;
  }, [listAggregateItems]);

  const primaryDisparoByCampaign = useMemo(
    () => buildPrimaryDisparoByCampaignMap(listAggregateDisparos),
    [listAggregateDisparos]
  );

  const campaignDisplayKindById = useMemo(() => {
    const m = new Map<string, CampaignListDisplayKind>();
    for (const c of campaigns) {
      if (!c.viewerCanManage) continue;
      const items = listItemsByCampaignId.get(c.campaignId) ?? [];
      const d = primaryDisparoByCampaign.get(c.campaignId);
      m.set(c.campaignId, campaignListDisplayKind(c, items, d));
    }
    return m;
  }, [campaigns, listItemsByCampaignId, primaryDisparoByCampaign]);

  const waMembersForPicker = useMemo(() => {
    const base = [...waMembers];
    const self = usuario;
    const selfPrincipal = self && (self.whatsappAccountId || '').trim();
    if (!self?.usuarioId || !selfPrincipal) {
      return base.sort((a, b) => {
        const byRole = USER_ROLE_PRIORITY[a.role] - USER_ROLE_PRIORITY[b.role];
        if (byRole !== 0) return byRole;
        return a.nome.localeCompare(b.nome);
      });
    }
    const already = base.some((u) => u.usuarioId === self.usuarioId);
    const mergeSelf =
      (self.role === 'atendente' && !already) || (waMembersLoadFailed && !already);
    const withSelf = mergeSelf ? [self, ...base] : base;
    return withSelf.sort((a, b) => {
      const byRole = USER_ROLE_PRIORITY[a.role] - USER_ROLE_PRIORITY[b.role];
      if (byRole !== 0) return byRole;
      return a.nome.localeCompare(b.nome);
    });
  }, [waMembers, usuario, waMembersLoadFailed]);

  const waMembersWithPrincipal = useMemo(
    () => waMembersForPicker.filter((u) => (u.whatsappAccountId || '').trim()),
    [waMembersForPicker]
  );

  const waMembersWithoutPrincipal = useMemo(
    () => waMembersForPicker.filter((u) => !(u.whatsappAccountId || '').trim()),
    [waMembersForPicker]
  );

  const selectableDispatchAccounts = useMemo(() => {
    if (!usuario) return waAccounts;
    if (['owner', 'admin', 'gerente', 'super_admin'].includes(usuario.role)) return waAccounts;
    const ids = [usuario.whatsappAccountId, usuario.whatsappAccountIdCampanha].filter(Boolean) as string[];
    return waAccounts.filter((a) => ids.includes(a.accountId));
  }, [usuario, waAccounts]);

  const detailPrincipalSelectedMember = useMemo(() => {
    const id = detailPrincipalUsuarioId.trim();
    if (!id) return undefined;
    return waMembersForPicker.find((u) => u.usuarioId === id);
  }, [detailPrincipalUsuarioId, waMembersForPicker]);

  const showDetailDelegateCheckbox =
    !!usuario &&
    ROLES_MAY_DELEGATE_CAMPAIGN_VISIBILITY.includes(usuario.role) &&
    detailPrincipalBlock &&
    detailPrincipalSelectedMember?.role === 'atendente';

  useEffect(() => {
    const id = detailPrincipalUsuarioId.trim();
    if (!id || !detailPrincipalBlock) {
      setDetailDelegateManagementToPrincipalMember(false);
      return;
    }
    const m = waMembersForPicker.find((u) => u.usuarioId === id);
    if (m && m.role !== 'atendente') {
      setDetailDelegateManagementToPrincipalMember(false);
    }
  }, [detailPrincipalBlock, detailPrincipalUsuarioId, waMembersForPicker]);

  const detailPrincipalOrphanOption = useMemo(() => {
    const id = detailPrincipalUsuarioId.trim();
    if (!id || waMembersForPicker.some((u) => u.usuarioId === id)) return null;
    return (
      <option key={`orphan-detail-${id}`} value={id}>
        Membro atual (lista incompleta)
      </option>
    );
  }, [detailPrincipalUsuarioId, waMembersForPicker]);

  const detailFullMessagePreview = useMemo(
    () =>
      buildFullCampaignDispatchPreview({
        mensagem: detailMensagem,
        principalBlockEnabled: detailPrincipalBlock,
        principalIntro: detailPrincipalIntro,
        principalUsuarioId: detailPrincipalUsuarioId,
        useGlobalUnsubscribe: detailUseGlobalUnsubscribeConfig,
        unsubscribeFooterTemplate: detailUnsubscribeFooterTemplate,
      }),
    [
      detailMensagem,
      detailPrincipalBlock,
      detailPrincipalIntro,
      detailPrincipalUsuarioId,
      detailUseGlobalUnsubscribeConfig,
      detailUnsubscribeFooterTemplate,
    ]
  );

  const syncDetailFieldsFromSelectedCampaign = useCallback(() => {
    if (!selectedCampaign) {
      setDetailNome('');
      setDetailMensagem('');
      setDetailPrincipalBlock(false);
      setDetailPrincipalIntro('');
      setDetailPrincipalUsuarioId('');
      setDetailDelegateManagementToPrincipalMember(false);
      setDetailAllowActivePosVendasDispatch(false);
      setDetailAttendanceMode('human_kanban');
      return;
    }
    const c = selectedCampaign;
    setDetailNome(c.nome);
    setDetailMensagem(c.mensagem);
    setDetailUseGlobalUnsubscribeConfig(c.unsubscribeUseGlobal !== false);
    setDetailUnsubscribeFooterTemplate(c.unsubscribeFooterTemplate || '');
    const uid = (c.principalWhatsappUsuarioId || '').trim();
    const explicitFlag =
      c.principalCallBlockEnabled === true || c.principalCallBlockEnabled === false;
    const blockOn = c.principalCallBlockEnabled === true || (uid.length > 0 && !explicitFlag);
    setDetailPrincipalBlock(blockOn);
    setDetailPrincipalUsuarioId(uid);
    setDetailPrincipalIntro(
      blockOn ? (c.principalCallIntroText || '').trim() || DEFAULT_PRINCIPAL_CALL_INTRO : ''
    );
    setDetailDispatchWhatsappAccountId((c.whatsappAccountId || '').trim());
    setDetailDelegateManagementToPrincipalMember(!!c.delegateManagementToPrincipalMember);
    setDetailAllowActivePosVendasDispatch(!!c.allowActivePosVendasDispatch);
    setDetailAttendanceMode(c.attendanceMode || 'human_kanban');
  }, [selectedCampaign]);

  useEffect(() => {
    syncDetailFieldsFromSelectedCampaign();
  }, [syncDetailFieldsFromSelectedCampaign]);

  /** Algum filtro de listagem fora do default? Controla o badge do botão. */
  const hasActiveListFilters =
    listPeriod !== 'all' ||
    listSearch.trim() !== '' ||
    filterStatus !== 'all' ||
    filterCreatedFrom.trim() !== '' ||
    filterCreatedTo.trim() !== '' ||
    filterUserId !== '' ||
    filterWhatsappAccountId !== '';

  const handleClearListFilters = useCallback(() => {
    setListPeriod('all');
    setListSearch('');
    setFilterStatus('all');
    setFilterCreatedFrom('');
    setFilterCreatedTo('');
    setFilterUserId('');
    setFilterWhatsappAccountId('');
  }, []);

  useEffect(() => {
    apiService.getWhatsAppAccounts().then(setWaAccounts).catch(() => setWaAccounts([]));
  }, []);

  const filteredCampaigns = useMemo(() => {
    // `listPeriod` é aplicado server-side em `apiService.listCampaigns({period})`
    // (ver `loadCampaigns`). Aqui só sobram filtros que não dá pra empurrar pro
    // backend (texto livre, status derivado, range custom, criador, conta WA).
    let list = campaigns.filter((c) => c.viewerCanManage === true);
    if (deferredListSearch) {
      list = list.filter((c) => {
        const nm = (c.nome || '').toLowerCase();
        const msg = (c.mensagem || '').toLowerCase();
        return nm.includes(deferredListSearch) || msg.includes(deferredListSearch);
      });
    }
    if (filterStatus !== 'all') {
      if (filterStatus === 'rascunho') {
        list = list.filter((c) => c.status === 'rascunho');
      } else if (filterStatus === 'encerrada') {
        list = list.filter((c) => c.status === 'encerrada');
      } else if (filterStatus === 'list_pendente') {
        list = list.filter((c) => campaignDisplayKindById.get(c.campaignId) === 'pendente');
      } else if (filterStatus === 'list_em_execucao') {
        list = list.filter((c) => campaignDisplayKindById.get(c.campaignId) === 'em_execucao');
      } else if (filterStatus === 'list_finalizada') {
        list = list.filter((c) => campaignDisplayKindById.get(c.campaignId) === 'finalizada');
      }
    }
    if (filterCreatedFrom.trim() || filterCreatedTo.trim()) {
      list = list.filter((c) => campaignCreatedInOptionalRange(c, filterCreatedFrom, filterCreatedTo));
    }
    if (filterUserId) {
      list = list.filter((c) => c.createdByUsuarioId === filterUserId);
    }
    if (filterWhatsappAccountId) {
      list = list.filter((c) => (c.whatsappAccountId || '').trim() === filterWhatsappAccountId);
    }
    return list;
  }, [
    campaigns,
    deferredListSearch,
    filterStatus,
    filterCreatedFrom,
    filterCreatedTo,
    filterUserId,
    filterWhatsappAccountId,
    campaignDisplayKindById,
  ]);

  const loadCampaigns = useCallback(async () => {
    let visibleIds: string[] = [];
    try {
      setLoading(true);
      // Etapa 1 — a lista vem rápida (filtrada por período no backend, PR #71).
      // Renderiza imediatamente; sem esperar pelos agregados.
      const list = await apiService.listCampaigns({ period: listPeriod });
      setCampaigns(list);
      visibleIds = list.map((c) => c.campaignId);
    } catch (err: any) {
      console.error('Erro ao carregar campanhas:', err);
    } finally {
      setLoading(false);
    }

    // Etapa 2 — items + disparos em background pra alimentar os badges
    // derivados (campaignListDisplayKind). Tela já está visível; o badge de
    // campanha ativa cai pro fallback "pendente" até items chegarem, depois
    // atualiza. Rascunho/encerrada ignoram items de qualquer jeito.
    if (visibleIds.length === 0) {
      setListAggregateItems([]);
      setListAggregateDisparos([]);
      return;
    }
    try {
      const [items, disparos] = await Promise.all([
        apiService.listCampaignItems({ campaignIds: visibleIds }),
        apiService.getDisparos(),
      ]);
      setListAggregateItems(items);
      setListAggregateDisparos(disparos);
    } catch (err) {
      console.error('Erro ao carregar agregados de campanhas:', err);
    }
  }, [listPeriod]);

  useEffect(() => {
    loadCampaigns();
  }, [loadCampaigns]);

  useEffect(() => {
    void (async () => {
      try {
        const list = await apiService.getUsuarios();
        const members = list
          .filter((u) => u.ativo !== false)
          .sort((a, b) => {
            const byRole = USER_ROLE_PRIORITY[a.role] - USER_ROLE_PRIORITY[b.role];
            if (byRole !== 0) return byRole;
            return a.nome.localeCompare(b.nome);
          });
        setWaMembers(members);
        setWaMembersLoadFailed(false);
      } catch {
        setWaMembers([]);
        setWaMembersLoadFailed(true);
      }
    })();
  }, []);

  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState === 'visible') void loadCampaigns();
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [loadCampaigns]);

  const handleSaveDetail = async () => {
    if (!selectedCampaign) return;
    const tn = detailNome.trim();
    const tm = detailMensagem.trim();
    if (!tn || !tm) {
      setError('Nome e mensagem não podem ficar vazios.');
      return;
    }
    const detailWa = detailDispatchWhatsappAccountId.trim();
    if (!detailWa && !(selectedCampaign.whatsappAccountId || '').trim()) {
      setError('Selecione a conta WhatsApp para disparos desta campanha.');
      return;
    }
    const detailDelegatePayload = (() => {
      if (!detailPrincipalBlock) return false;
      if (showDetailDelegateCheckbox) return detailDelegateManagementToPrincipalMember;
      const m = detailPrincipalSelectedMember;
      if (m && m.role !== 'atendente') return false;
      return !!selectedCampaign.delegateManagementToPrincipalMember;
    })();

    try {
      setSaving(true);
      setError(null);
      const updated = await apiService.patchCampaign(selectedCampaign.campaignId, {
        nome: tn,
        mensagem: tm,
        ...(detailWa ? { whatsappAccountId: detailWa } : {}),
        ...(detailPrincipalBlock
          ? {
              principalCallBlockEnabled: true,
              principalWhatsappUsuarioId: detailPrincipalUsuarioId.trim() || null,
              principalCallIntroText: detailPrincipalIntro.trim() || null,
              delegateManagementToPrincipalMember: detailDelegatePayload,
            }
          : {
              principalCallBlockEnabled: false,
              principalWhatsappUsuarioId: null,
              principalCallIntroText: null,
              delegateManagementToPrincipalMember: false,
            }),
        unsubscribeUseGlobal: detailUseGlobalUnsubscribeConfig,
        ...(detailUseGlobalUnsubscribeConfig
          ? {}
          : {
              unsubscribeOverrideEnabled: true,
              unsubscribeBaseUrl: '',
              unsubscribeFooterTemplate: detailUnsubscribeFooterTemplate,
            }),
        allowActivePosVendasDispatch: detailAllowActivePosVendasDispatch,
        attendanceMode: detailAttendanceMode,
      });
      setCampaigns((prev) => prev.map((c) => (c.campaignId === updated.campaignId ? updated : c)));
      setDetailInfoEditing(false);
      setFeedback('Alterações salvas.');
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Erro ao salvar campanha.');
    } finally {
      setSaving(false);
    }
  };

  const handleCloseCampaignFor = async (c: Campaign) => {
    if (c.status !== 'ativa') return;
    const ok = window.confirm(
      'Encerrar esta campanha? O disparo será concluído. Itens pendentes: lost/importados voltam às origens com item marcado Encerrado; parceiros só terão o item encerrado (sem alterar o cadastro do parceiro). A campanha deixa de aparecer no Kanban de Campanhas.'
    );
    if (!ok) return;
    try {
      setSaving(true);
      setError(null);
      const updated = await apiService.patchCampaign(c.campaignId, {
        status: 'encerrada',
      });
      setCampaigns((prev) => prev.map((x) => (x.campaignId === updated.campaignId ? updated : x)));
      setFeedback('Campanha encerrada.');
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Erro ao encerrar campanha.');
    } finally {
      setSaving(false);
    }
  };

  const handleReopenCampaign = async () => {
    if (!selectedCampaign || selectedCampaign.status !== 'encerrada') return;
    const ok = window.confirm(
      'Reabrir campanha? Disparos anteriores já podem ter sido concluídos; será preciso criar novo disparo se desejar envios automáticos.'
    );
    if (!ok) return;
    try {
      setSaving(true);
      setError(null);
      const updated = await apiService.patchCampaign(selectedCampaign.campaignId, {
        status: 'ativa',
      });
      setCampaigns((prev) => prev.map((c) => (c.campaignId === updated.campaignId ? updated : c)));
      setFeedback('Campanha reaberta.');
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Erro ao reabrir campanha.');
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (managerView !== 'detail' || !selectedCampaignId) {
      setDetailCampaignItems([]);
      setDetailItemsLoading(false);
      return;
    }
    let cancel = false;
    setDetailItemsLoading(true);
    void (async () => {
      try {
        const items = await apiService.listCampaignItems({ campaignId: selectedCampaignId });
        if (!cancel) setDetailCampaignItems(items);
      } catch {
        if (!cancel) setDetailCampaignItems([]);
      } finally {
        if (!cancel) setDetailItemsLoading(false);
      }
    })();
    return () => {
      cancel = true;
    };
  }, [managerView, selectedCampaignId]);

  useEffect(() => {
    setDetailInfoEditing(false);
  }, [selectedCampaignId]);

  useEffect(() => {
    if (managerView !== 'detail' || !selectedCampaignId) {
      setDetailDisparos([]);
      setDetailDisparosLoading(false);
      return;
    }
    let cancel = false;
    setDetailDisparosLoading(true);
    void (async () => {
      try {
        const all = await apiService.getDisparos();
        const mine = (all || []).filter((d) => d.campaignId === selectedCampaignId);
        if (!cancel) setDetailDisparos(mine);
      } catch {
        if (!cancel) setDetailDisparos([]);
      } finally {
        if (!cancel) setDetailDisparosLoading(false);
      }
    })();
    return () => {
      cancel = true;
    };
  }, [managerView, selectedCampaignId]);

  const detailPrimaryDisparo = useMemo(
    () => pickPrimaryDisparoForCampaign(detailDisparos),
    [detailDisparos]
  );

  const detailCanPauseDisparo = detailPrimaryDisparo?.status === 'em_execucao';
  const detailCanResumeDisparo =
    !!detailPrimaryDisparo &&
    ['criado', 'pausado', 'pausado_horario'].includes(detailPrimaryDisparo.status);

  const refreshDetailDisparos = useCallback(async () => {
    if (!selectedCampaignId) return;
    try {
      const all = await apiService.getDisparos();
      setDetailDisparos((all || []).filter((d) => d.campaignId === selectedCampaignId));
    } catch {
      /* ignorado */
    }
  }, [selectedCampaignId]);

  const handleDetailDisparoPauseOrResume = useCallback(async () => {
    const d = detailPrimaryDisparo;
    if (!d) return;
    if (d.status !== 'em_execucao' && !['criado', 'pausado', 'pausado_horario'].includes(d.status)) {
      return;
    }
    try {
      setSaving(true);
      setError(null);
      if (d.status === 'em_execucao') {
        await apiService.updateDisparoStatus(d.disparoId, 'pausado');
        setFeedback('Disparo pausado.');
      } else {
        await apiService.updateDisparoStatus(d.disparoId, 'em_execucao');
        setFeedback('Disparo retomado.');
      }
      await refreshDetailDisparos();
      void loadCampaigns();
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Erro ao atualizar disparo.');
    } finally {
      setSaving(false);
    }
  }, [detailPrimaryDisparo, refreshDetailDisparos, loadCampaigns]);

  const handleDuplicateCampaign = useCallback(async () => {
    if (!selectedCampaign) return;
    try {
      setSaving(true);
      setError(null);
      const created = await apiService.createCampaign(buildCreateCampaignDuplicateBody(selectedCampaign));
      setCampaigns((prev) => {
        const rest = prev.filter((c) => c.campaignId !== created.campaignId);
        return [created, ...rest];
      });
      setFeedback('Rascunho duplicado; edite no assistente (passo 1).');
      setWizardEditId(created.campaignId);
      setWizardOpen(true);
      setManagerView('list');
      setSelectedCampaignId(null);
      void loadCampaigns();
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Erro ao duplicar campanha.');
    } finally {
      setSaving(false);
    }
  }, [selectedCampaign, loadCampaigns]);

  const detailListDisplayKind = useMemo((): CampaignListDisplayKind | null => {
    if (!selectedCampaign) return null;
    const fromListItems = listItemsByCampaignId.get(selectedCampaign.campaignId) ?? [];
    const primaryD = primaryDisparoByCampaign.get(selectedCampaign.campaignId);
    if (!detailItemsLoading) {
      return campaignListDisplayKind(
        selectedCampaign,
        detailCampaignItems,
        pickPrimaryDisparoForCampaign(detailDisparos)
      );
    }
    return campaignListDisplayKind(selectedCampaign, fromListItems, primaryD);
  }, [
    selectedCampaign,
    detailItemsLoading,
    detailCampaignItems,
    detailDisparos,
    listItemsByCampaignId,
    primaryDisparoByCampaign,
  ]);

  const detailProgressPct = useMemo(
    () => campaignDispatchProgressPercent(detailCampaignItems),
    [detailCampaignItems]
  );

  const detailSourceAggregate = useMemo(
    () => aggregateCampaignItemSourceTypes(detailCampaignItems),
    [detailCampaignItems]
  );

  const detailLeadTotalDisplay = useMemo(() => {
    if (!selectedCampaign) return 0;
    return campaignTotalLeadCount(selectedCampaign, detailCampaignItems.length);
  }, [selectedCampaign, detailCampaignItems.length]);

  const readOnlyDispatchLineLabel = useMemo(() => {
    if (!selectedCampaign) return '—';
    const id = (selectedCampaign.whatsappAccountId || '').trim();
    if (!id) return '—';
    const acc = waAccounts.find((a) => a.accountId === id);
    return acc ? formatWhatsappSelectLabel(acc, waMembersForPicker) : id;
  }, [selectedCampaign, waAccounts, waMembersForPicker]);

  const readOnlyPrincipalMemberName = useMemo(() => {
    if (!selectedCampaign) return '';
    const uid = (selectedCampaign.principalWhatsappUsuarioId || '').trim();
    if (!uid) return '';
    return waMembersForPicker.find((u) => u.usuarioId === uid)?.nome?.trim() || 'Membro selecionado';
  }, [selectedCampaign, waMembersForPicker]);

  const readOnlyCreatorName = useMemo(() => {
    if (!selectedCampaign) return '';
    const id = selectedCampaign.createdByUsuarioId;
    return waMembersForPicker.find((u) => u.usuarioId === id)?.nome?.trim() || '—';
  }, [selectedCampaign, waMembersForPicker]);

  const disparoWhatsappLabel = useMemo(() => {
    if (!detailPrimaryDisparo) return '—';
    const id = (detailPrimaryDisparo.whatsappAccountId || '').trim();
    if (!id) return '—';
    const acc = waAccounts.find((a) => a.accountId === id);
    return acc ? formatWhatsappSelectLabel(acc, waMembersForPicker) : id;
  }, [detailPrimaryDisparo, waAccounts, waMembersForPicker]);

  if (wizardOpen) {
    return (
      <CampaignWizard
        key={wizardEditId || 'nova'}
        initialCampaignId={wizardEditId}
        onClose={() => {
          setWizardOpen(false);
          setWizardEditId(null);
          void loadCampaigns();
        }}
        onComplete={({ goToKanban }) => {
          setWizardOpen(false);
          setWizardEditId(null);
          void loadCampaigns();
          if (goToKanban) onOpenExecution?.();
        }}
      />
    );
  }

  return (
    <section className="campaign-manager-module">
      <header className="campaign-manager-header">
        <div>
          <h2>Campanhas</h2>
          <p>Lista de campanhas. Use Nova campanha para o assistente em quatro passos (dados, público, disparo e revisão).</p>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <button
            type="button"
            className="campaign-primary-button"
            onClick={() => {
              setWizardEditId(null);
              setWizardOpen(true);
            }}
          >
            Nova campanha
          </button>
        </div>
      </header>

      {error && <div className="campaign-error">{error}</div>}
      {feedback && <div className="campaign-feedback">{feedback}</div>}

      {managerView === 'detail' && selectedCampaign ? (
        <div className="campaign-manager-detail-view">
          <nav className="campaign-detail-breadcrumb" aria-label="Navegação">
            <button
              type="button"
              className="campaign-back-link"
              onClick={() => {
                setManagerView('list');
                setDetailTab('info');
              }}
            >
              ← Campanhas existentes
            </button>
          </nav>
          <header className="campaign-detail-page-head">
            <div className="campaign-detail-page-head-text">
              <h3 className="campaign-detail-page-title">{selectedCampaign.nome}</h3>
              <p className="campaign-detail-page-sub">
                Criada em{' '}
                {new Date(selectedCampaign.createdAt).toLocaleDateString('pt-BR', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                })}
              </p>
            </div>
            <div className="campaign-detail-page-head-actions">
              {detailListDisplayKind === 'em_execucao' ? (
                detailCanPauseDisparo || detailCanResumeDisparo ? (
                  <button
                    type="button"
                    className="campaign-detail-ghost-btn"
                    disabled={saving}
                    title={
                      detailCanPauseDisparo
                        ? 'Pausar a fila de disparo (mesma ação que Pause no Kanban ou no Histórico)'
                        : 'Retomar a fila de disparo (mesma ação que Play no Kanban)'
                    }
                    onClick={() => void handleDetailDisparoPauseOrResume()}
                  >
                    {detailCanPauseDisparo ? (
                      <Pause size={16} strokeWidth={2} aria-hidden />
                    ) : (
                      <Play size={16} strokeWidth={2} aria-hidden />
                    )}
                    {detailCanPauseDisparo ? 'Pausar campanha' : 'Retomar disparo'}
                  </button>
                ) : (
                  <button
                    type="button"
                    className="campaign-detail-ghost-btn"
                    disabled
                    title="Nenhum disparo neste estado; use Execução (Kanban) para criar ou iniciar a fila (Play)."
                  >
                    <Pause size={16} strokeWidth={2} aria-hidden />
                    Pausar campanha
                  </button>
                )
              ) : null}
              <button
                type="button"
                className="campaign-detail-ghost-btn"
                disabled={saving}
                title="Cria um rascunho com os mesmos dados gerais (passo 1 do assistente) e abre o assistente"
                onClick={() => void handleDuplicateCampaign()}
              >
                <Copy size={16} strokeWidth={2} aria-hidden />
                Duplicar
              </button>
              <button
                type="button"
                className="campaign-detail-ghost-btn"
                disabled={saving || selectedCampaign.status !== 'ativa'}
                title={
                  selectedCampaign.status === 'ativa'
                    ? 'Encerrar campanha'
                    : selectedCampaign.status === 'encerrada'
                      ? 'Campanha já encerrada'
                      : 'Disponível após a campanha estar ativa (enviada para pendentes)'
                }
                onClick={() => void handleCloseCampaignFor(selectedCampaign)}
              >
                <Ban size={16} strokeWidth={2} aria-hidden />
                Encerrar
              </button>
            </div>
          </header>

          <div className="campaign-detail-metrics-grid" aria-label="Resumo da campanha">
            <div className="campaign-detail-metric-card">
              <span className="campaign-detail-metric-card-label">Status</span>
              {detailListDisplayKind ? (
                <span
                  className={`campaign-status-badge ${campaignListDisplayBadge(detailListDisplayKind).className}`}
                >
                  {campaignListDisplayBadge(detailListDisplayKind).label}
                </span>
              ) : (
                <span className="campaign-status-badge status-rascunho">—</span>
              )}
            </div>
            <div className="campaign-detail-metric-card">
              <span className="campaign-detail-metric-card-label">Total de leads</span>
              <strong className="campaign-detail-metric-card-value">{detailLeadTotalDisplay}</strong>
            </div>
            <div className="campaign-detail-metric-card">
              <span className="campaign-detail-metric-card-label">Progresso</span>
              <strong className="campaign-detail-metric-card-value">{detailProgressPct}%</strong>
            </div>
            <div className="campaign-detail-metric-card">
              <span className="campaign-detail-metric-card-label">Fontes</span>
              <div className="campaign-detail-metric-card-pills">
                {campaignOriginLabels(selectedCampaign.origem).length === 0 ? (
                  <span className="campaign-detail-muted">—</span>
                ) : (
                  campaignOriginLabels(selectedCampaign.origem).map((p) => (
                    <span key={p} className="campaign-detail-pill campaign-detail-pill--accent">
                      {p}
                    </span>
                  ))
                )}
              </div>
            </div>
          </div>

          <section className="campaign-detail-progress-card" aria-labelledby="campaign-detail-progress-heading">
            <div className="campaign-detail-progress-card-inner">
              <h4 id="campaign-detail-progress-heading" className="campaign-detail-progress-title">
                Progresso da campanha
              </h4>
              <div className="campaign-detail-progress-row">
                <div className="campaign-detail-progress-track" role="progressbar" aria-valuenow={detailProgressPct} aria-valuemin={0} aria-valuemax={100}>
                  <div
                    className="campaign-detail-progress-fill"
                    style={{ width: `${Math.min(100, Math.max(0, detailProgressPct))}%` }}
                  />
                </div>
                <span className="campaign-detail-progress-pct">{detailProgressPct}%</span>
              </div>
            </div>
          </section>

          <div className="campaign-detail-tabs" role="tablist" aria-label="Secções da campanha">
            <button
              type="button"
              role="tab"
              aria-selected={detailTab === 'info'}
              className={`campaign-detail-tab ${detailTab === 'info' ? 'active' : ''}`}
              onClick={() => setDetailTab('info')}
            >
              Informações
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={detailTab === 'leads'}
              className={`campaign-detail-tab ${detailTab === 'leads' ? 'active' : ''}`}
              onClick={() => setDetailTab('leads')}
            >
              Itens da campanha ({detailCampaignItems.length})
            </button>
          </div>
          {detailTab === 'info' ? (
            <div className="campaign-detail-info-tab">
              <div className="campaign-detail-info-columns">
                <div className="campaign-detail-info-col campaign-detail-info-col--main">
                  <section className="campaign-detail-card">
                    <div className="campaign-detail-card-head">
                      <h4 className="campaign-detail-card-title">Dados da campanha</h4>
                      {!detailInfoEditing ? (
                        <button
                          type="button"
                          className="campaign-detail-edit-btn"
                          onClick={() => setDetailInfoEditing(true)}
                        >
                          Editar
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="campaign-detail-edit-btn campaign-detail-edit-btn--muted"
                          disabled={saving}
                          onClick={() => {
                            syncDetailFieldsFromSelectedCampaign();
                            setDetailInfoEditing(false);
                          }}
                        >
                          Cancelar edição
                        </button>
                      )}
                    </div>
                    {!detailInfoEditing ? (
                      <div className="campaign-detail-readonly">
                        <div className="campaign-detail-readonly-block">
                          <span className="campaign-detail-readonly-label">Mensagem</span>
                          <div className="campaign-detail-readonly-message">
                            {(selectedCampaign.mensagem || '').trim() || '(Vazio)'}
                          </div>
                        </div>
                        {(selectedCampaign.principalCallBlockEnabled === true ||
                          !!(selectedCampaign.principalWhatsappUsuarioId || '').trim()) ? (
                          <div className="campaign-detail-readonly-block">
                            <span className="campaign-detail-readonly-label">Ligue para</span>
                            <p className="campaign-detail-readonly-p">{readOnlyPrincipalMemberName || '—'}</p>
                            {(selectedCampaign.principalCallIntroText || '').trim() ? (
                              <p className="campaign-detail-readonly-p campaign-detail-readonly-p--muted">
                                {(selectedCampaign.principalCallIntroText || '').trim()}
                              </p>
                            ) : null}
                            <p className="campaign-detail-readonly-linknote">
                              Link wa.me gerado automaticamente no envio.
                            </p>
                          </div>
                        ) : null}
                        <div className="campaign-detail-readonly-block">
                          <span className="campaign-detail-readonly-label">Rodapé de descadastro</span>
                          <p className="campaign-detail-readonly-p">
                            {selectedCampaign.unsubscribeUseGlobal !== false
                              ? 'Texto e URL conforme Configurações da organização.'
                              : (selectedCampaign.unsubscribeFooterTemplate || '').trim() ||
                                'Personalizado — se o modelo estiver vazio, usa-se o texto das Configurações.'}
                          </p>
                        </div>
                        <div className="campaign-detail-readonly-block">
                          <span className="campaign-detail-readonly-label">Linha WhatsApp</span>
                          <p className="campaign-detail-readonly-p">{readOnlyDispatchLineLabel}</p>
                        </div>
                        <div className="campaign-detail-readonly-block">
                          <span className="campaign-detail-readonly-label">Pós-vendas</span>
                          <p className="campaign-detail-readonly-p">
                            {selectedCampaign.allowActivePosVendasDispatch
                              ? 'Ativa: permite disparo para números com pós-vendas ativo na organização.'
                              : 'Inativa: por omissão não dispara para números em pós-vendas ativo.'}
                          </p>
                        </div>
                      </div>
                    ) : (
                      <div className="campaign-detail-card-body">
                        <div className="campaign-form-panel campaign-detail-form-inner">
                          <div className="campaign-form-group">
                            <label htmlFor="detail-nome">Nome</label>
                            <input
                              id="detail-nome"
                              type="text"
                              value={detailNome}
                              onChange={(e) => setDetailNome(e.target.value)}
                              disabled={saving}
                            />
                          </div>
                          <div className="campaign-form-group">
                            <label htmlFor="detail-mensagem">Mensagem</label>
                            <textarea
                              id="detail-mensagem"
                              value={detailMensagem}
                              onChange={(e) => setDetailMensagem(e.target.value)}
                              rows={3}
                              disabled={saving}
                            />
                            <small className="campaign-form-hint">{PRINCIPAL_WHATSAPP_HELP}</small>
                          </div>
                          <div className="campaign-form-group">
                            <label htmlFor="detail-dispatch-wa">Linha padrão WhatsApp (disparos)</label>
                            <select
                              id="detail-dispatch-wa"
                              value={detailDispatchWhatsappAccountId}
                              onChange={(e) => setDetailDispatchWhatsappAccountId(e.target.value)}
                              disabled={saving || selectableDispatchAccounts.length === 0}
                            >
                              <option value="">
                                {(selectedCampaign.whatsappAccountId || '').trim()
                                  ? 'Trocar linha de envio…'
                                  : 'Selecione a linha de envio…'}
                              </option>
                              {selectableDispatchAccounts.map((a) => (
                                <option key={a.accountId} value={a.accountId}>
                                  {formatWhatsappSelectLabel(a, waMembersForPicker)}
                                </option>
                              ))}
                            </select>
                            <small className="campaign-form-hint">{DISPATCH_WHATSAPP_HELP}</small>
                          </div>
                          <div className="campaign-form-group">
                            <label className="campaign-delegate-management-row">
                              <input
                                type="checkbox"
                                checked={detailAllowActivePosVendasDispatch}
                                onChange={(e) => setDetailAllowActivePosVendasDispatch(e.target.checked)}
                                disabled={saving}
                              />
                              <span>Permitir disparo para números já em pós-vendas ativo na organização</span>
                            </label>
                            <small className="campaign-form-hint">{POS_VENDAS_DISPATCH_HELP}</small>
                          </div>
                          <div className="campaign-form-group">
                            <label htmlFor="detail-attendance-mode">Tipo de atendimento (campanha)</label>
                            <select
                              id="detail-attendance-mode"
                              value={detailAttendanceMode}
                              onChange={(e) => setDetailAttendanceMode(e.target.value as CampaignAttendanceMode)}
                              disabled={saving}
                            >
                              <option value="human_kanban">Kanban humano (campanha)</option>
                              <option value="ai_funnel">Funil IA (campanha)</option>
                            </select>
                            <small className="campaign-form-hint">
                              Funil IA usa o Policy router no webhook; o harness completo evolui por fases.
                            </small>
                          </div>
                          <div className="campaign-form-group">
                            <fieldset className="campaign-unsub-fieldset" disabled={saving}>
                              <legend className="campaign-unsub-legend">Ligue para</legend>
                              <label className="campaign-unsub-label-row">
                                <input
                                  type="checkbox"
                                  checked={detailPrincipalBlock}
                                  onChange={(e) => {
                                    const on = e.target.checked;
                                    setDetailPrincipalBlock(on);
                                    if (on && !detailPrincipalIntro.trim()) {
                                      setDetailPrincipalIntro(DEFAULT_PRINCIPAL_CALL_INTRO);
                                    }
                                    if (!on) {
                                      setDetailPrincipalIntro('');
                                      setDetailPrincipalUsuarioId('');
                                    }
                                  }}
                                />
                                <span className="campaign-unsub-label-text">
                                  Adicionar Ligue para
                                  <button
                                    type="button"
                                    className="campaign-info-btn"
                                    aria-label="Sobre Ligue para"
                                    title={PRINCIPAL_WHATSAPP_HELP}
                                    aria-describedby="campaign-principal-hint-detail"
                                    disabled={saving}
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                    }}
                                  >
                                    i
                                  </button>
                                </span>
                              </label>
                              <span id="campaign-principal-hint-detail" className="campaign-sr-only">
                                {PRINCIPAL_WHATSAPP_HELP}
                              </span>
                            </fieldset>
                          </div>
                          {detailPrincipalBlock && (
                            <div className="campaign-form-group campaign-principal-picker">
                              <label htmlFor="detail-principal-intro">Texto antes do link</label>
                              <textarea
                                id="detail-principal-intro"
                                value={detailPrincipalIntro}
                                onChange={(e) => setDetailPrincipalIntro(e.target.value)}
                                rows={2}
                                disabled={saving}
                                placeholder={DEFAULT_PRINCIPAL_CALL_INTRO}
                              />
                              <small className="campaign-form-hint">
                                Na linha seguinte do disparo será inserido o link ({'{usePrincipalWhatsapp}'}).
                              </small>
                              <label htmlFor="detail-principal-wa-user">Ligue para:</label>
                              <select
                                id="detail-principal-wa-user"
                                value={detailPrincipalUsuarioId}
                                onChange={(e) => setDetailPrincipalUsuarioId(e.target.value)}
                                disabled={saving}
                              >
                                <option value="">Selecione um membro…</option>
                                {detailPrincipalOrphanOption}
                                {waMembersWithPrincipal.length > 0 && (
                                  <optgroup label="Com WhatsApp principal">
                                    {waMembersWithPrincipal.map((u) => {
                                      const isSelf = u.usuarioId === usuario?.usuarioId;
                                      return (
                                        <option key={u.usuarioId} value={u.usuarioId}>
                                          {isSelf ? `Eu (${u.nome})` : `${u.nome} (${USER_ROLE_LABELS[u.role]})`}
                                        </option>
                                      );
                                    })}
                                  </optgroup>
                                )}
                                {waMembersWithoutPrincipal.length > 0 && (
                                  <optgroup label="Sem WhatsApp principal (indisponível)">
                                    {waMembersWithoutPrincipal.map((u) => (
                                      <option key={u.usuarioId} value={u.usuarioId} disabled>
                                        {u.nome} ({USER_ROLE_LABELS[u.role]})
                                      </option>
                                    ))}
                                  </optgroup>
                                )}
                              </select>
                              {showDetailDelegateCheckbox ? (
                                <>
                                  <label className="campaign-delegate-management-row">
                                    <input
                                      type="checkbox"
                                      checked={detailDelegateManagementToPrincipalMember}
                                      onChange={(e) =>
                                        setDetailDelegateManagementToPrincipalMember(e.target.checked)
                                      }
                                      disabled={saving}
                                    />
                                    <span>
                                      Permitir disparo e Kanban ao atendente do «Ligue para» (após haver contactos)
                                    </span>
                                  </label>
                                  <small className="campaign-form-hint">
                                    O atendente indicado passa a ver a campanha no Kanban depois de existir pelo menos
                                    um contacto na campanha. A edição completa continua só para quem criou a campanha
                                    (e gestores no âmbito habitual).
                                  </small>
                                </>
                              ) : null}
                              {waMembersLoadFailed ? (
                                <small className="campaign-form-hint">
                                  Não foi possível carregar a lista de membros. Administradores e gerentes podem
                                  atualizar esta opção.
                                </small>
                              ) : waMembersWithPrincipal.length === 0 ? (
                                <small className="campaign-form-hint">
                                  Nenhum membro com conta WhatsApp principal cadastrada.
                                </small>
                              ) : waMembersWithoutPrincipal.length > 0 ? (
                                <small className="campaign-form-hint">
                                  Usuários sem WhatsApp principal ficam visíveis, mas não podem ser selecionados.
                                </small>
                              ) : null}
                            </div>
                          )}
                          <div className="campaign-form-group">
                            <fieldset className="campaign-unsub-fieldset" disabled={saving}>
                              <legend className="campaign-unsub-legend">Rodapé de descadastro</legend>
                              <label className="campaign-unsub-label-row">
                                <input
                                  type="radio"
                                  name="campaign-unsub-detail"
                                  checked={detailUseGlobalUnsubscribeConfig}
                                  onChange={() => setDetailUseGlobalUnsubscribeConfig(true)}
                                />
                                <span className="campaign-unsub-label-text">
                                  Usar texto e URL das Configurações
                                  <button
                                    type="button"
                                    className="campaign-info-btn"
                                    aria-label="Sobre descadastro global"
                                    title={UNSUBSCRIBE_GLOBAL_HELP}
                                    aria-describedby="campaign-unsub-hint-detail"
                                    disabled={saving}
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                    }}
                                  >
                                    i
                                  </button>
                                </span>
                              </label>
                              <span id="campaign-unsub-hint-detail" className="campaign-sr-only">
                                {UNSUBSCRIBE_GLOBAL_HELP}
                              </span>
                              <label className="campaign-unsub-label-row campaign-unsub-label-row--second">
                                <input
                                  type="radio"
                                  name="campaign-unsub-detail"
                                  checked={!detailUseGlobalUnsubscribeConfig}
                                  onChange={() => setDetailUseGlobalUnsubscribeConfig(false)}
                                />
                                <span>Personalizar texto do rodapé nesta campanha</span>
                              </label>
                            </fieldset>
                          </div>
                          {!detailUseGlobalUnsubscribeConfig && (
                            <div className="campaign-form-group">
                              <label>Template do rodapé (opcional)</label>
                              <textarea
                                value={detailUnsubscribeFooterTemplate}
                                onChange={(e) => setDetailUnsubscribeFooterTemplate(e.target.value)}
                                rows={3}
                                disabled={saving}
                                placeholder="Opcional"
                              />
                              <small className="campaign-form-hint">
                                Use {'{unsubscribeUrl}'} para inserir o link. A URL base do link vem das Configurações. Se
                                o template ficar vazio, usamos o texto das Configurações.
                              </small>
                            </div>
                          )}
                          <div className="campaign-detail-actions">
                            <button
                              type="button"
                              className="campaign-secondary-button"
                              disabled={saving}
                              onClick={() => void handleSaveDetail()}
                            >
                              Salvar
                            </button>
                            {selectedCampaign.status === 'rascunho' && (
                              <button
                                type="button"
                                className="campaign-primary-button"
                                disabled={saving}
                                onClick={() => {
                                  setWizardEditId(selectedCampaign.campaignId);
                                  setWizardOpen(true);
                                }}
                              >
                                Continuar assistente
                              </button>
                            )}
                            {selectedCampaign.status === 'encerrada' && (
                              <button
                                type="button"
                                className="campaign-secondary-button"
                                disabled={saving}
                                onClick={() => void handleReopenCampaign()}
                              >
                                Reabrir campanha
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </section>

                  <section className="campaign-detail-card campaign-detail-card--disparo">
                    <h4 className="campaign-detail-card-title">Configurações de disparo</h4>
                    {detailDisparosLoading ? (
                      <p className="campaign-detail-muted">A carregar disparos…</p>
                    ) : detailPrimaryDisparo ? (
                      <>
                        <dl className="campaign-detail-dl">
                          <dt>Conta WhatsApp</dt>
                          <dd>{disparoWhatsappLabel}</dd>
                          <dt>Intervalo entre envios</dt>
                          <dd>{formatDisparoIntervaloLabel(detailPrimaryDisparo.intervaloMinutos)}</dd>
                          <dt>Horário</dt>
                          <dd>
                            {detailPrimaryDisparo.horaInicio} – {detailPrimaryDisparo.horaFim}
                          </dd>
                          <dt>Pausar após (envios/rodada)</dt>
                          <dd>{detailPrimaryDisparo.maxDisparosPorNumero}</dd>
                        </dl>
                        <div className="campaign-detail-resp">
                          <span className="campaign-detail-readonly-label">Responsáveis</span>
                          <div className="campaign-detail-avatars" aria-label="Criador do disparo e da campanha">
                            <span
                              className="campaign-detail-avatar"
                              title={detailPrimaryDisparo.createdByNome || 'Disparo'}
                            >
                              {initialsFromNome(detailPrimaryDisparo.createdByNome || '?')}
                            </span>
                            {readOnlyCreatorName !== '—' &&
                            (detailPrimaryDisparo.createdByNome || '').trim().toLowerCase() !==
                              readOnlyCreatorName.trim().toLowerCase() ? (
                              <span className="campaign-detail-avatar" title={`Campanha: ${readOnlyCreatorName}`}>
                                {initialsFromNome(readOnlyCreatorName)}
                              </span>
                            ) : null}
                          </div>
                        </div>
                      </>
                    ) : (
                      <p className="campaign-detail-muted">
                        Ainda não há fila de disparo. Use <strong>Play</strong> no Kanban de execução para criar a fila
                        com a linha WhatsApp desta campanha.
                      </p>
                    )}
                  </section>
                </div>

                <div className="campaign-detail-info-col campaign-detail-info-col--side">
                  <section
                    className="campaign-detail-card campaign-detail-card--preview"
                    aria-label="Prévia da mensagem completa do disparo"
                  >
                    <h4 className="campaign-detail-card-title">Prévia da mensagem completa</h4>
                    <pre className="campaign-detail-preview-pre" tabIndex={0}>
                      {detailFullMessagePreview}
                    </pre>
                    <small className="campaign-form-hint">
                      Ordem no envio: mensagem, bloco Ligue para (se ativo), rodapé de descadastro. URLs são exemplos.
                    </small>
                  </section>
                </div>
              </div>

              <section className="campaign-detail-card campaign-detail-card--rules">
                <h4 className="campaign-detail-card-title">Regras automáticas</h4>
                <p className="campaign-detail-rules-prazo">
                  <strong>Prazo para remarketing:</strong>{' '}
                  {organization?.defaultRemarketingMeses != null &&
                  Number.isInteger(organization.defaultRemarketingMeses) &&
                  organization.defaultRemarketingMeses >= 1
                    ? `${organization.defaultRemarketingMeses} meses.`
                    : 'Conforme Configurações (período não definido).'}
                </p>
                <p className="campaign-detail-rules-desc">
                  Configurado pelo administrador. Leads sem sucesso poderão ser incluídos em novas campanhas após este
                  período desde a última tentativa de contacto.
                </p>
                {Object.keys(detailSourceAggregate).length > 0 ? (
                  <p className="campaign-detail-source-aggregate campaign-detail-source-aggregate--inrules">
                    Itens por tipo:{' '}
                    {Object.entries(detailSourceAggregate)
                      .map(([k, v]) => `${k}: ${v}`)
                      .join(' · ')}
                  </p>
                ) : null}
              </section>
            </div>
          ) : (
            <div className="campaign-detail-leads-tab">
              {detailItemsLoading ? (
                <p className="campaign-empty">Carregando itens…</p>
              ) : (
                <CampaignItemsListPanel items={detailCampaignItems} showRemarketingHint />
              )}
            </div>
          )}
        </div>
      ) : (
      <div className="campaign-manager-grid campaign-manager-grid--list-only">
        <section className="campaign-list-panel">
          <div className="campaign-list-panel-head">
            <h3>Campanhas existentes</h3>
            <div className="campaign-list-filters" role="toolbar" aria-label="Filtrar por período de criação">
              {(Object.keys(LIST_PERIOD_LABELS) as ListPeriod[]).map((period) => (
                <button
                  key={period}
                  type="button"
                  className={`campaign-period-btn ${listPeriod === period ? 'active' : ''}`}
                  onClick={() => setListPeriod(period)}
                >
                  {LIST_PERIOD_LABELS[period]}
                </button>
              ))}
            </div>
          </div>
          <div className="campaign-list-search-row">
            <label className="campaign-list-search campaign-list-search--grow">
              <span className="campaign-sr-only">Buscar campanhas</span>
              <input
                type="search"
                value={listSearch}
                onChange={(e) => setListSearch(e.target.value)}
                placeholder="Buscar por nome ou mensagem…"
              />
            </label>
            <button
              type="button"
              className="campaign-filters-advanced-btn"
              aria-expanded={advancedFiltersOpen}
              aria-controls="campaign-advanced-fields-panel"
              title={advancedFiltersOpen ? 'Ocultar filtros avançados' : 'Mostrar filtros avançados'}
              onClick={() => setAdvancedFiltersOpen((v) => !v)}
            >
              <Filter size={16} strokeWidth={2} aria-hidden />
              Filtros avançados
            </button>
            <button
              type="button"
              className="campaign-filters-clear-btn"
              disabled={!hasActiveListFilters}
              title="Resetar período, busca e filtros avançados"
              onClick={handleClearListFilters}
            >
              Limpar filtros
            </button>
          </div>
          <div className="campaign-advanced-filters-wrap">
            {advancedFiltersOpen && (
              <div
                id="campaign-advanced-fields-panel"
                className="campaign-advanced-fields"
                role="region"
                aria-label="Filtros avançados"
              >
                <label className="campaign-adv-field">
                  <span>Criada desde</span>
                  <input type="date" value={filterCreatedFrom} onChange={(e) => setFilterCreatedFrom(e.target.value)} />
                </label>
                <label className="campaign-adv-field">
                  <span>Criada até</span>
                  <input type="date" value={filterCreatedTo} onChange={(e) => setFilterCreatedTo(e.target.value)} />
                </label>
                <label className="campaign-adv-field">
                  <span>Estado</span>
                  <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value as StatusFilter)}>
                    <option value="all">Todos</option>
                    <option value="rascunho">Rascunho</option>
                    <option value="encerrada">Encerrada</option>
                    <option value="list_pendente">Pendente (execução)</option>
                    <option value="list_em_execucao">Em execução</option>
                    <option value="list_finalizada">Finalizada (pipeline)</option>
                  </select>
                </label>
                <label className="campaign-adv-field">
                  <span>Responsável (criador)</span>
                  <select value={filterUserId} onChange={(e) => setFilterUserId(e.target.value)}>
                    <option value="">Todos</option>
                    {waMembersForPicker.map((u) => (
                      <option key={u.usuarioId} value={u.usuarioId}>
                        {u.nome}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="campaign-adv-field">
                  <span>Conta WhatsApp</span>
                  <select
                    value={filterWhatsappAccountId}
                    onChange={(e) => setFilterWhatsappAccountId(e.target.value)}
                  >
                    <option value="">Todas</option>
                    {waAccounts.map((a) => (
                      <option key={a.accountId} value={a.accountId}>
                        {a.nome} — {a.telefone}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
            )}
          </div>
          <p className="campaign-list-count" aria-live="polite">
            {filteredCampaigns.length}{' '}
            {filteredCampaigns.length === 1 ? 'campanha encontrada' : 'campanhas encontradas'}
          </p>
          <div className="campaign-manager-table-wrap">
            {loading ? (
              <p className="campaign-empty">Carregando campanhas...</p>
            ) : filteredCampaigns.length === 0 ? (
              <p className="campaign-empty">Nenhuma campanha corresponde aos filtros.</p>
            ) : (
              <table className="campaign-manager-table">
                <thead>
                  <tr>
                    <th>Campanha</th>
                    <th>Status</th>
                    <th>Leads</th>
                    <th>Fontes</th>
                    <th>Criada em</th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCampaigns.map((c) => {
                    const rowKind =
                      campaignDisplayKindById.get(c.campaignId) ??
                      campaignListDisplayKind(c, [], undefined);
                    const rowBadge = campaignListDisplayBadge(rowKind);
                    return (
                    <tr key={c.campaignId} className={selectedCampaignId === c.campaignId ? 'is-selected' : ''}>
                      <td>
                        <div className="cm-table-campaign-cell">
                          <div className="cm-table-campaign-title-row">
                            <strong>{c.nome}</strong>
                            {c.attendanceMode === 'ai_funnel' && (
                              <span className="cm-campaign-ia-badge" title="Atendimento por IA (funil)">
                                IA
                              </span>
                            )}
                          </div>
                          <span className="cm-table-snippet">{messageSnippet(c.mensagem)}</span>
                        </div>
                      </td>
                      <td>
                        <span className={`campaign-status-badge ${rowBadge.className}`}>
                          {rowBadge.label}
                        </span>
                      </td>
                      <td>{c.campaignItemCount ?? '—'}</td>
                      <td>
                        <span className="cm-table-sources">
                          {campaignOriginLabels(c.origem).join(', ') || '—'}
                        </span>
                      </td>
                      <td>{new Date(c.createdAt).toLocaleDateString('pt-BR')}</td>
                      <td>
                        <div className="cm-table-actions">
                          <button
                            type="button"
                            className="campaign-secondary-button"
                            onClick={() => {
                              setSelectedCampaignId(c.campaignId);
                              setManagerView('detail');
                              setDetailTab('info');
                              setError(null);
                              setFeedback('');
                            }}
                          >
                            Ver detalhes
                          </button>
                          {c.status === 'rascunho' && (
                            <button
                              type="button"
                              className="campaign-primary-button"
                              onClick={() => {
                                setWizardEditId(c.campaignId);
                                setWizardOpen(true);
                              }}
                            >
                              Assistente
                            </button>
                          )}
                          {c.status === 'ativa' && (
                            <button
                              type="button"
                              className="campaign-danger-button"
                              disabled={saving}
                              onClick={(e) => {
                                e.stopPropagation();
                                void handleCloseCampaignFor(c);
                              }}
                            >
                              Encerrar
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </section>
      </div>
      )}
    </section>
  );
};
